/** A chronological thread — case log, conversation, history.
 *
 * The console and the portal both render the same case log and had each grown
 * their own stack of cards for it. A timeline says what a stack of cards does
 * not: that these entries are one sequence, in order, with a shape that makes
 * the odd one out visible at a glance.
 *
 * Ordering and grouping belong to the caller. This renders what it is given.
 */
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '../../lib/utils'

export type TimelineTone = 'neutral' | 'accent' | 'warning' | 'success' | 'critical'

const MARKER: Record<TimelineTone, string> = {
  neutral: 'border-border bg-card text-muted-foreground',
  accent: 'border-primary/40 bg-primary/10 text-primary',
  warning: 'border-warning/40 bg-warning/10 text-warning',
  success: 'border-success/40 bg-success/10 text-success',
  critical: 'border-critical/40 bg-critical/10 text-critical',
}

const PANEL: Record<TimelineTone, string> = {
  neutral: 'border-border bg-card',
  accent: 'border-primary/30 bg-card',
  warning: 'border-warning/40 bg-warning/5',
  success: 'border-success/30 bg-card',
  critical: 'border-critical/40 bg-critical/5',
}

export function Timeline({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <ol className={cn('space-y-2', className)}>{children}</ol>
}

export function TimelineItem({
  icon: Icon,
  tone = 'neutral',
  title,
  meta,
  children,
  className,
}: {
  icon?: LucideIcon
  tone?: TimelineTone
  /** Who or what — the left of the header line. */
  title: ReactNode
  /** When, and anything else qualifying it — the right of the header line. */
  meta?: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <li
      className={cn(
        // The rail is the item's own pseudo-element so the last entry can drop
        // it without the caller counting rows.
        'relative pl-8 last:before:hidden',
        "before:absolute before:left-[0.6875rem] before:top-7 before:bottom-0 before:w-px before:bg-border before:content-['']",
        className,
      )}
    >
      <span
        className={cn(
          'absolute left-0 top-0.5 grid size-6 place-items-center rounded-full border',
          MARKER[tone],
        )}
        aria-hidden
      >
        {Icon ? <Icon className="size-3" /> : <span className="size-1.5 rounded-full bg-current" />}
      </span>

      <div className={cn('rounded-md border px-3 py-2.5', PANEL[tone])}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium">{title}</span>
          {meta && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              {meta}
            </span>
          )}
        </div>
        {children && <div className="mt-1.5">{children}</div>}
      </div>
    </li>
  )
}
